'use client';

import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
} from 'react';

type AnimatedLinesModule = typeof import('./AnimatedLinesCanvas');

const AnimatedLinesBackground = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [AnimatedLinesCanvas, setAnimatedLinesCanvas] =
    useState<ComponentType | null>(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      setShouldLoad(true);
      return;
    }

    // On desktop, load immediately
    if (!isMobile) {
      setShouldLoad(true);
      return;
    }

    // On mobile, use Intersection Observer with delay
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '200px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile]);

  const loadCanvas = useCallback(async () => {
    if (AnimatedLinesCanvas) return;
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) return;

    const animatedModule: AnimatedLinesModule = await import(
      './AnimatedLinesCanvas'
    );
    setAnimatedLinesCanvas(() => animatedModule.default);
  }, [AnimatedLinesCanvas]);

  useEffect(() => {
    if (!shouldLoad || AnimatedLinesCanvas) return;
    if (typeof window === 'undefined') return;

    let cancelled = false;
    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const start = () => {
      if (cancelled) return;
      loadCanvas().catch(() => {
        /* no-op */
      });
    };

    const win = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (
          cb: IdleRequestCallback,
          opts?: IdleRequestOptions,
        ) => number;
        cancelIdleCallback?: (handle: number) => void;
      };

    if (win.requestIdleCallback) {
      const idleTimeout = isMobile ? 3000 : 1500;
      idleId = win.requestIdleCallback(start, { timeout: idleTimeout });
    } else {
      const fallbackDelay = isMobile ? 2000 : 800;
      timeoutId = win.setTimeout(start, fallbackDelay);
    }

    return () => {
      cancelled = true;
      if (idleId !== null) {
        win.cancelIdleCallback?.(idleId);
      }
      if (timeoutId !== null) {
        win.clearTimeout(timeoutId);
      }
    };
  }, [AnimatedLinesCanvas, loadCanvas, shouldLoad, isMobile]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: -1 }}
    >
      {AnimatedLinesCanvas ? <AnimatedLinesCanvas /> : null}
    </div>
  );
};

export default memo(AnimatedLinesBackground);
